// src/components/PLM.jsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import styles from "../styles/PLMApp.module.css";

import { PLM_CATEGORIES, LOGO_URL } from "../data/constants";
import { getStatusLabel } from "../utils/utils";
import { useWorkflowSteps } from "../hooks/hooks";
import {
  saveStep,
  logActivity,
  archiveOrder,
  cancelOrder,
} from "../firebase/firebaseHelpers";

import Sidebar from "./plm/Sidebar";
import HomeDashboard from "./plm/HomeDashboard";
import WorkflowBoard from "./plm/WorkflowBoard";
import TimelineView from "./plm/GanttChart";
import KanbanView from "./plm/KanbanView";
import DocumentCenter from "./plm/DocumentCenter";
import StepDrawer from "./plm/StepDrawer";
import Toast from "./plm/Toast";
import PastOrders from "./plm/PastOrders";
import ActiveOrders from "./plm/ActiveOrders";

export default function PLMApp() {
  const { supplierId } = useParams();
  const activeSupplierId = supplierId ?? auth.currentUser?.uid;

  const [activeView, setActiveView] = useState("activeorders");
  const [workflowSubView, setWorkflowSubView] = useState("rows");

  const [orders, setOrders] = useState([]);
  const [activeOrderId, setActiveOrderId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const { steps, loading } = useWorkflowSteps(activeSupplierId, activeOrderId);

  const [drawerState, setDrawerState] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Fetch isAdmin from the logged-in user's doc (never from URL param) ───
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      getDoc(doc(db, "users", user.uid)).then((snap) => {
        setIsAdmin(snap.data()?.isAdmin === true);
      });
    });
    return unsub;
  }, []);

  // ── Live orders listener ─────────────────────────────────────────────────
  useEffect(() => {
    if (!activeSupplierId) return;

    const q = query(
      collection(db, "users", activeSupplierId, "orders"),
      where("status", "==", "active"),
    );

    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setOrders(arr);

      // Auto-select: keep existing selection if still valid and still active,
      // otherwise pick the first non-archived, non-cancelled order
      setActiveOrderId((prev) => {
        const prevOrder = arr.find((o) => o.id === prev);
        if (prevOrder && !prevOrder.archived && !prevOrder.cancelled)
          return prev;

        const firstActive = arr.find((o) => !o.archived && !o.cancelled);
        return firstActive?.id || null;
      });
    });

    return unsub;
  }, [activeSupplierId]);

  const activeOrder = useMemo(
    () => orders.find((o) => o.id === activeOrderId) || null,
    [orders, activeOrderId],
  );

  const hasActiveOrder = !!activeOrderId;
  const safeSteps = hasActiveOrder ? steps : {};

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  // ── Step handlers ────────────────────────────────────────────────────────
  const handleOpenStep = useCallback(
    (docId, index) => {
      if (!activeOrderId) return;

      const parts = docId.replace(`${activeSupplierId}_`, "").split("_");
      const catId = parts[0];
      const stepIdx = parseInt(parts[1]) || 0;
      const cat = PLM_CATEGORIES.find((c) => c.id === catId);

      setDrawerState({
        docId,
        index: stepIdx,
        category: cat,
        stepTitle: cat?.steps[stepIdx] || "Step",
      });
    },
    [activeSupplierId, activeOrderId],
  );

  const handleStatusChange = useCallback(
    async (docId, newStatus) => {
      if (!activeOrderId) return;

      const parts = docId.replace(`${activeSupplierId}_`, "").split("_");
      const catId = parts[0];
      const stepIdx = parseInt(parts[1]) || 0;
      const cat = PLM_CATEGORIES.find((c) => c.id === catId);

      await saveStep(activeSupplierId, activeOrderId, docId, {
        productId: activeSupplierId,
        orderId: activeOrderId,
        categoryId: catId,
        stepNumber: stepIdx + 1,
        stepTitle: cat?.steps[stepIdx] || "",
        status: newStatus,
      });

      await logActivity(
        activeSupplierId,
        activeOrderId,
        docId,
        "Status changed",
        `→ ${getStatusLabel(newStatus)}`,
      );

      showToast(`Status updated: ${getStatusLabel(newStatus)}`);
    },
    [activeSupplierId, activeOrderId],
  );

  // ── Archive handler ──────────────────────────────────────────────────────
  const handleArchive = useCallback(
    async (orderId) => {
      try {
        await archiveOrder(activeSupplierId, orderId);
        // If the archived order was selected, clear selection
        if (orderId === activeOrderId) setActiveOrderId(null);
        showToast("Order archived successfully");
      } catch (e) {
        console.error(e);
        showToast("Failed to archive order");
      }
    },
    [activeSupplierId, activeOrderId],
  );

  // ── Cancel handler ───────────────────────────────────────────────────────
  const handleCancel = useCallback(
    async (orderId) => {
      try {
        await cancelOrder(activeSupplierId, orderId);
        // If the cancelled order was selected, clear selection
        if (orderId === activeOrderId) setActiveOrderId(null);
        showToast("Order cancelled");
      } catch (e) {
        console.error(e);
        showToast("Failed to cancel order");
      }
    },
    [activeSupplierId, activeOrderId],
  );

  const drawerStepData = drawerState
    ? safeSteps[drawerState.docId] || {
        status: "not_started",
        stepTitle: drawerState.stepTitle,
        stepNumber: drawerState.index + 1,
      }
    : null;

  const pageTitles = {
    activeorders: { title: "Active Orders", sub: "Active Orders" },
    home: { title: "Dashboard", sub: "Product Lifecycle Overview" },
    workflow: { title: "Workflow", sub: "Operational Process Board" },
    timeline: { title: "Timeline", sub: "Gantt Schedule Overview" },
    kanban: { title: "Kanban", sub: "Status Summary Board" },
    documents: { title: "Documents", sub: "Centralized File Management" },
    pastorders: { title: "Past Orders", sub: "Order History & Archives" },
  };

  const currentPage = pageTitles[activeView] || pageTitles.home;

  return (
    <div className={styles.app}>
      <nav className={styles.topNav}>
        <div className={styles.navLogo}>
          <Link
            to="/"
            className={styles.topLogo}
            aria-label="Synaptex — go to homepage"
          >
            <img
              src={LOGO_URL}
              alt="Synaptex logo"
              className={styles.logoImg}
            />
          </Link>
        </div>

        <div className={styles.navRight}>
          <Link
            to="/"
            className={styles.navBtn}
            onClick={() => setActiveView("home")}
          >
            Home
          </Link>

          <Link
            to="/login"
            className={`${styles.navBtn} ${styles.navBtnActive}`}
            onClick={async () => await signOut(auth)}
          >
            Log Out
          </Link>
        </div>
      </nav>

      <div className={styles.shell}>
        <Sidebar
          active={activeView}
          onNavigate={setActiveView}
          steps={safeSteps}
          supplierId={activeSupplierId}
        />

        <main className={styles.main}>
          <div className={styles.pageHeader}>
            <div className={styles.pageTitle}>{currentPage.title}</div>
            <div className={styles.pageSubtitle}>{currentPage.sub}</div>
            <div className={styles.pageRule} />
          </div>

          {loading && hasActiveOrder && (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 12,
                letterSpacing: 2,
                padding: 32,
              }}
            >
              Loading workflow…
            </div>
          )}

          {!loading && activeView === "activeorders" && (
            <ActiveOrders
              orders={orders}
              activeOrderId={activeOrderId}
              onSelectOrder={setActiveOrderId}
              onArchive={handleArchive}
              onCancel={handleCancel}
              isAdmin={isAdmin}
            />
          )}

          {!loading && activeView === "pastorders" && (
            <PastOrders orders={orders} />
          )}

          {!loading && activeView === "home" && (
            <HomeDashboard
              steps={safeSteps}
              onNavigate={setActiveView}
              supplierId={activeSupplierId}
              orderProfile={activeOrder || null}
              isAdmin={isAdmin}
            />
          )}

          {!loading &&
            activeView === "workflow" &&
            workflowSubView === "rows" && (
              <WorkflowBoard
                steps={safeSteps}
                onOpenStep={handleOpenStep}
                onStatusChange={handleStatusChange}
                supplierId={activeSupplierId}
                statusDisabled={!hasActiveOrder}
                isAdmin={isAdmin}
              />
            )}

          {!loading &&
            activeView === "workflow" &&
            workflowSubView === "timeline" && (
              <TimelineView steps={safeSteps} supplierId={activeSupplierId} />
            )}

          {!loading &&
            activeView === "workflow" &&
            workflowSubView === "kanban" && (
              <KanbanView
                steps={safeSteps}
                onOpenStep={handleOpenStep}
                supplierId={activeSupplierId}
                interactionDisabled={!hasActiveOrder}
              />
            )}

          {!loading && activeView === "timeline" && (
            <TimelineView steps={safeSteps} supplierId={activeSupplierId} />
          )}

          {!loading && activeView === "kanban" && (
            <KanbanView
              steps={safeSteps}
              onOpenStep={handleOpenStep}
              supplierId={activeSupplierId}
              interactionDisabled={!hasActiveOrder}
            />
          )}

          {activeView === "documents" && (
            <DocumentCenter
              supplierId={activeSupplierId}
              orderId={activeOrderId || null}
              interactionDisabled={!hasActiveOrder}
            />
          )}
        </main>
      </div>

      {drawerState && drawerStepData && activeOrderId && (
        <StepDrawer
          step={{
            ...drawerStepData,
            stepTitle: drawerState.stepTitle,
            stepNumber: drawerState.index + 1,
          }}
          supplierId={activeSupplierId}
          orderId={activeOrderId}
          docId={drawerState.docId}
          category={drawerState.category}
          onClose={() => setDrawerState(null)}
          onStatusChange={(s) => handleStatusChange(drawerState.docId, s)}
          isAdmin={isAdmin}
        />
      )}

      <Toast message={toast} />
    </div>
  );
}
